import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {distribution} from 'metrics/Metrics';
import APIUtil from 'util/APIUtil';

import {FileAnalyticsParams, getFileAnalyticsMetadata} from './FileAnalyticsUtils';

export enum GridInsertionMethod {
  Toolbar = 'toolbar',
  DragAndDrop = 'drag_and_drop',
}

export enum FileAddMethod {
  UploadButton = 'upload_button',
  DragAndDrop = 'drag_and_drop',
}

export enum InsertionOutcome {
  AllSucceeded = 'all_succeeded',
  SomeFailed = 'some_failed',
  AllFailed = 'all_failed',
}

function getDocumentType(fileId: string | null | undefined): string {
  if (!fileId) {
    return 'unknown';
  }
  return APIUtil.apiIdToMaybeType(fileId) ?? 'unknown';
}

export function trackInsertedNotePartGrid(
  fileAnalyticsParams: FileAnalyticsParams,
  insertionMethod: GridInsertionMethod,
  previewableCount: number,
  nonPreviewableCount: number,
  gridProvenanceUuid?: string
): void {
  const totalCount = previewableCount + nonPreviewableCount;
  const fileMetadata = getFileAnalyticsMetadata(fileAnalyticsParams);
  const documentType = getDocumentType(fileAnalyticsParams.fileId);

  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.INSERTED_NOTE_PART_GRID, {
    ...fileMetadata,
    insertion_method: insertionMethod,
    previewable_attachment_count: previewableCount,
    non_previewable_attachment_count: nonPreviewableCount,
    total_attachment_count: totalCount,
    document_type: documentType,
    grid_provenance_uuid: gridProvenanceUuid,
  });

  if (previewableCount > 0) {
    distribution('notebook.note_part_grid.attachments_per_insert', previewableCount, {
      insertion_method: insertionMethod,
      document_type: documentType,
    });
  }
}

export function trackAddedFilesToNotePartGrid(
  fileId: string | null | undefined,
  fileAddMethod: FileAddMethod,
  previewableCount: number,
  nonPreviewableCount: number,
  existingFileCount: number,
  gridProvenanceUuid?: string
): void {
  const totalAdded = previewableCount + nonPreviewableCount;
  const documentType = getDocumentType(fileId);

  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.ADDED_FILES_TO_NOTE_PART_GRID, {
    file_add_method: fileAddMethod,
    previewable_count: previewableCount,
    non_previewable_count: nonPreviewableCount,
    total_added: totalAdded,
    existing_file_count: existingFileCount,
    resulting_file_count: existingFileCount + previewableCount,
    document_type: documentType,
    grid_provenance_uuid: gridProvenanceUuid,
  });

  if (previewableCount > 0) {
    distribution('notebook.note_part_grid.attachments_per_insert', previewableCount, {
      file_add_method: fileAddMethod,
      document_type: documentType,
    });
  }
}

export function trackRemovedFileFromGrid(
  fileId: string | null | undefined,
  remainingFileCount: number,
  gridProvenanceUuid?: string
): void {
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.REMOVED_FILE_FROM_GRID, {
    remaining_file_count: remainingFileCount,
    document_type: getDocumentType(fileId),
    grid_provenance_uuid: gridProvenanceUuid,
  });
}

export function trackReorderedFilesInGrid(
  fileId: string | null | undefined,
  fileCount: number,
  gridProvenanceUuid?: string
): void {
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.REORDERED_FILES_IN_GRID, {
    file_count: fileCount,
    document_type: getDocumentType(fileId),
    grid_provenance_uuid: gridProvenanceUuid,
  });
}

/**
 * Track how long it takes for a batch of file uploads to fully settle
 * (all succeed or fail). Uses Promise.allSettled so the timer captures
 * the full duration regardless of individual failures.
 */
export async function trackBatchUploadDuration(
  deferreds: Array<JQueryPromise<unknown>>,
  startTime: number,
  fileId: string | null | undefined
): Promise<void> {
  if (deferreds.length === 0) {
    return;
  }

  // jQuery deferreds are "thenable" but Promise.allSettled requires native
  // Promise instances, so we wrap each deferred explicitly.
  const promises = deferreds.map(
    (d) =>
      new Promise<void>((resolve, reject) => {
        d.done(() => {
          resolve();
        }).fail(() => {
          reject();
        });
      })
  );

  const results = await Promise.allSettled(promises);
  const durationMs = performance.now() - startTime;
  const failCount = results.filter((r) => r.status === 'rejected').length;
  let outcome: InsertionOutcome;
  if (failCount === 0) {
    outcome = InsertionOutcome.AllSucceeded;
  } else if (failCount === results.length) {
    outcome = InsertionOutcome.AllFailed;
  } else {
    outcome = InsertionOutcome.SomeFailed;
  }
  trackGridInsertionDuration(fileId, durationMs, deferreds.length, outcome);
}

function trackGridInsertionDuration(
  fileId: string | null | undefined,
  durationMs: number,
  fileCount: number,
  outcome: InsertionOutcome
): void {
  distribution('notebook.note_part_grid.insertion_duration_ms', durationMs, {
    document_type: getDocumentType(fileId),
    file_count: fileCount,
    outcome,
  });
}
